
import { Pill, Droplet } from 'lucide-react';
import { Medication } from './index';

export const womensHealthMedications: Medication[] = [
  {
    id: '19',
    name: 'Estradiol',
    description: 'A key estrogen hormone essential for bone health, mood stability, and overall well-being. Often used in hormone replacement therapy (HRT).',
    research: 'Estradiol in HRT - Estradiol, the primary estrogen, relieves menopausal symptoms (hot flashes, bone loss) via HRT. Studies show it reduces osteoporosis risk by 30-50% with long-term use.',
    url: 'https://journals.lww.com/menopausejournal/Abstract/2016/05000/Estradiol_based_menopausal_hormone_therapy_and.5.aspx',
    category: 'womens-health',
    Icon: Pill,
    video: 'https://www.youtube.com/watch?v=2h2KxYfGJzY',
    clinicalDetails: {
      dosingGuidelines: '0.5-2 mg PO daily or 0.025-0.1 mg/day transdermal patch; compounded creams 0.25-0.5 mg/g.',
      administrationProtocols: 'Oral daily, patch 1-2x weekly, or topical cream daily to thin skin areas.',
      contraindications: 'Breast cancer, VTE history, undiagnosed vaginal bleeding, active liver disease.',
      monitoringRequirements: 'Monitor menopausal symptoms, breast exams, endometrial thickness; assess bleeding, lipids.'
    },
    availablePreparations: 'Topical Cream (e.g., 0.5 mg/gram; with Lidocaine at 0.5 mg/gram).',
    adverseReactions: {
      common: 'Breast tenderness, headache, nausea.',
      lessCommon: 'Bloating, mood changes, hypertension.',
      rare: 'Thromboembolism, breast cancer, gallbladder disease.'
    }
  },
  {
    id: '20',
    name: 'Estriol Vaginal Cream',
    description: 'A milder estrogen therapy used for vaginal atrophy, dryness, and menopausal symptoms.',
    research: 'Estriol for Vaginal Atrophy - Estriol cream treats vaginal atrophy in postmenopausal women, improving epithelial thickness and dryness with minimal systemic absorption. Trials confirm efficacy and safety.',
    url: 'https://www.tandfonline.com/doi/abs/10.1080/13697137.2017.1421928',
    category: 'womens-health',
    Icon: Droplet,
    clinicalDetails: {
      dosingGuidelines: '0.5-1 mg/g cream, 0.5-2g applied vaginally 1-3x weekly (compounded).',
      administrationProtocols: 'Vaginal application with applicator, preferably at bedtime.',
      contraindications: 'Hormone-dependent cancers, undiagnosed vaginal bleeding, thrombophlebitis.',
      monitoringRequirements: 'Monitor vaginal symptoms, bleeding; assess irritation, systemic effects.'
    },
    availablePreparations: 'Vaginal Cream (e.g., 0.2 mg/gram).',
    adverseReactions: {
      common: 'Vaginal irritation, discharge, itching.',
      lessCommon: 'Headache, breast pain.',
      rare: 'Allergic reactions, systemic estrogen effects.'
    }
  },
  {
    id: '21',
    name: 'Lidocaine/Estradiol Cream',
    description: 'A compounded formula for localized pain relief and estrogen therapy, often used for menopausal-related discomfort.',
    research: 'Compounded HRT Creams - Lidocaine/Estradiol cream combines local anesthesia with estrogen to relieve vulvovaginal pain and atrophy. Case studies show improved comfort in menopausal women.',
    url: 'https://ijpc.com/Abstracts/Abstract.cfm?ABS=3921',
    category: 'womens-health',
    Icon: Droplet,
    clinicalDetails: {
      dosingGuidelines: '2-5% lidocaine with 0.01-0.1% estradiol cream, applied daily (compounded).',
      administrationProtocols: 'Apply thin layer to affected areas; wash hands after application.',
      contraindications: 'Known sensitivity to ingredients, estrogen-dependent cancers, active DVT.',
      monitoringRequirements: 'Monitor pain relief, estrogen effects; check skin irritation, systemic absorption.'
    },
    availablePreparations: 'Topical Cream (e.g., 5%/0.5 mg/gram).',
    adverseReactions: {
      common: 'Skin irritation, burning, itching.',
      lessCommon: 'Systemic lidocaine effects (dizziness), estrogen absorption.',
      rare: 'Allergic reactions, methemoglobinemia (lidocaine).'
    }
  },
  {
    id: '22',
    name: 'Amitriptyline/Baclofen/Gabapentin Cream',
    description: 'A topical pain management combination that targets nerve pain and muscle relaxation.',
    research: 'Topical Pain Management - This compounded cream targets neuropathic pain and muscle spasms topically. Studies show 50-70% pain reduction in localized conditions, with minimal systemic effects.',
    url: 'https://academic.oup.com/painmedicine/article/17/8/1495/2581638',
    category: 'womens-health',
    Icon: Droplet,
    clinicalDetails: {
      dosingGuidelines: '2-4% amitriptyline, 2% baclofen, 6-10% gabapentin cream, applied 3-4x daily (compounded).',
      administrationProtocols: 'Apply thin layer to affected areas; avoid broken skin.',
      contraindications: 'Hypersensitivity to components, pregnancy (limited safety data).',
      monitoringRequirements: 'Monitor pain relief, skin irritation; assess for systemic absorption symptoms.'
    },
    availablePreparations: 'Topical Cream (e.g., 2%/2%/2%).',
    adverseReactions: {
      common: 'Skin irritation, drowsiness, dry mouth.',
      lessCommon: 'Dizziness, blurred vision.',
      rare: 'Allergic reactions, systemic absorption effects.'
    }
  },
  {
    id: '23',
    name: 'Scream Cream (Sildenafil/Aminophylline/Arginine)',
    description: 'A topical formulation containing Sildenafil, Aminophylline, and Arginine designed to enhance female sexual arousal and sensitivity.',
    research: 'Topical Sexual Enhancement - Scream Cream enhances female arousal by improving genital blood flow and sensitivity. Pilot studies suggest 60% improvement in sexual satisfaction with this compound.',
    url: 'https://www.jsm.jsexmed.org/article/S1743-6095(15)31254-6/fulltext',
    category: 'womens-health',
    Icon: Droplet,
    clinicalDetails: {
      dosingGuidelines: 'Typical formulation: 2-5% sildenafil, 2% aminophylline, 2% arginine; apply 30 min before activity (compounded).',
      administrationProtocols: 'Apply pea-sized amount to clitoral area; wash hands after application.',
      contraindications: 'Cardiovascular disease, nitrate medication use, pregnancy, active infections.',
      monitoringRequirements: 'Monitor arousal response, skin irritation; assess systemic PDE5 effects (e.g., headache).'
    },
    availablePreparations: 'Topical Cream (e.g., 2%/3%/6%).',
    adverseReactions: {
      common: 'Skin irritation, headache, flushing.',
      lessCommon: 'Dizziness, nausea.',
      rare: 'Priapism (clitoral), allergic reactions.'
    }
  }
];
